import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Form } from "antd";
import {
  EnrichmentModelForPreview,
  EnrichmentModelSelectedStatus,
  ModelTypes,
  SelectionAlertModel,
} from "types";

import {
  useGetModelsListApiV1EnrichmentDatasetIdListModelsGet,
  useTriggerPipelineApiV1IngestionTriggerPipelinePost,
} from "vl-api/vlComponents";

export interface useV3DataCreationTypes {
  isModelListLoading: boolean;
  modelList: EnrichmentModelForPreview[];
  selectedModelIds: string[];
  fetchEnrichmentModels: () => void;
  handleModelSelection: (modelId: string) => void;
  handleAcceptOverrideConfirmation: () => void;
  handleDeclineConfirmation: () => void;
  selectionAlertModel: SelectionAlertModel | null;
  modelDependencyMessages: Record<string, string>;
  disabledModelIds: string[];
  triggerEnrichmentPipeline: () => Promise<{
    success: boolean;
    error?: string;
  }>;
}

export const useV3DatasetCreation = (
  form?: ReturnType<typeof Form.useForm>[0],
  batchId?: string,
): useV3DataCreationTypes => {
  const { newDatasetId: datasetId } = useParams();

  // Create a local form instance as fallback to ensure hooks are always called
  const [localForm] = Form.useForm();
  const formInstance = form || localForm;

  // Always call Form.useWatch with a form instance (never conditionally)
  const watchedModelIds = Form.useWatch("selectedModelIds", formInstance);

  const selectedModelIds = useMemo(
    () => watchedModelIds || [],
    [watchedModelIds],
  );
  const [selectionAlertModel, setSelectionAlertModel] =
    useState<SelectionAlertModel | null>(null);
  const [overrideTargetModel, setOverrideTargetModel] =
    useState<EnrichmentModelForPreview | null>(null);

  const {
    data: rawModelData = [],
    isLoading: isModelListLoading,
    refetch: fetchEnrichmentModels,
  } = useGetModelsListApiV1EnrichmentDatasetIdListModelsGet(
    { pathParams: { datasetId: datasetId ?? "" } },
    { enabled: false },
  );

  // Normalize models for V3 (business rule enforcement)
  const modelList = useMemo(
    () =>
      rawModelData.map((model) => ({
        ...model,
        deletable: { value: false, reason: "Deletion not required in V3" },
        editable: { value: false, reason: "Editing not required in V3" },
      })) as EnrichmentModelForPreview[],
    [rawModelData],
  );

  const enrichmentModels = useMemo(() => {
    return selectedModelIds.reduce(
      (acc: Partial<Record<ModelTypes, string>>, modelId: string) => {
        const model = modelList.find((m) => m.id === modelId);
        if (model?.type) {
          acc[model.type] = model.id;
        }
        return acc;
      },
      {} as Partial<Record<ModelTypes, string>>,
    );
  }, [selectedModelIds, modelList]);

  const hasEnrichmentModels = !!Object.keys(enrichmentModels).length;

  const triggerPipelineMutation =
    useTriggerPipelineApiV1IngestionTriggerPipelinePost();

  const triggerEnrichmentPipeline = useCallback(async () => {
    if (!datasetId) {
      throw new Error("No dataset ID available");
    }

    const baseQueryParams = {
      path: formInstance.getFieldValue("path"),
      dataset_id: datasetId,
      ...(hasEnrichmentModels && {
        config_dict: JSON.stringify({
          enrichment_models: enrichmentModels,
        }),
      }),
    };

    const queryParams = batchId
      ? { ...baseQueryParams, batch_id: batchId }
      : baseQueryParams;

    try {
      await triggerPipelineMutation.mutateAsync({
        queryParams,
      });
      return { success: true };
    } catch (error) {
      console.error("Failed to trigger pipeline:", error);
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Unknown error";

      return { success: false, error: message };
    }
  }, [
    datasetId,
    formInstance,
    enrichmentModels,
    triggerPipelineMutation,
    hasEnrichmentModels,
    batchId,
  ]);

  const findParents = useCallback(
    (model: EnrichmentModelForPreview) =>
      modelList.filter(
        (m) => model.dependencies?.includes(m.type) && m.status === "Active",
      ),
    [modelList],
  );

  const areParentsSelected = useCallback(
    (model: EnrichmentModelForPreview) => {
      const parents = findParents(model);
      return parents.every((parent) => selectedModelIds.includes(parent.id));
    },
    [findParents, selectedModelIds],
  );

  const findChildren = useCallback(
    (model: EnrichmentModelForPreview) =>
      modelList.filter(
        (m) => m.dependencies?.includes(model.type) && m.status === "Active",
      ),
    [modelList],
  );

  const hasSelectedChildren = useCallback(
    (model: EnrichmentModelForPreview) =>
      findChildren(model).some((child) => selectedModelIds.includes(child.id)),
    [findChildren, selectedModelIds],
  );

  const modelDependencyMessages = useMemo(() => {
    const messages: Record<string, string> = {};

    modelList.forEach((model) => {
      const parents = findParents(model);
      const missing = parents.filter((p) => !selectedModelIds.includes(p.id));

      if (missing.length > 0) {
        const parentName = missing[0].name;
        messages[model.id] =
          `${parentName} is required for ${model.name}. Please select ${parentName} first.`;
      }
    });

    return messages;
  }, [modelList, selectedModelIds, findParents]);

  const disabledModelIds = useMemo(
    () =>
      modelList
        .filter(
          (model) =>
            selectedModelIds.includes(model.id) && hasSelectedChildren(model),
        )
        .map((model) => model.id),
    [modelList, selectedModelIds, hasSelectedChildren],
  );

  const updateSelectedModelIds = useCallback(
    (updater: (prev: string[]) => string[]) => {
      if (form) {
        const current = form.getFieldValue("selectedModelIds") || [];
        const updated = updater(current);
        form.setFieldsValue({ selectedModelIds: updated });
      }
    },
    [form],
  );

  const handleDeclineConfirmation = useCallback(() => {
    setSelectionAlertModel(null);
    setOverrideTargetModel(null);
  }, []);

  const handleAcceptOverrideConfirmation = useCallback(() => {
    if (overrideTargetModel) {
      updateSelectedModelIds((prev) =>
        prev.filter((id) => id !== overrideTargetModel.id),
      );
    }

    if (selectionAlertModel) {
      updateSelectedModelIds((prev) =>
        prev.includes(selectionAlertModel.id)
          ? prev
          : [...prev, selectionAlertModel.id],
      );
    }

    handleDeclineConfirmation();
  }, [
    overrideTargetModel,
    selectionAlertModel,
    handleDeclineConfirmation,
    updateSelectedModelIds,
  ]);

  const handleModelSelection = useCallback(
    (modelId: string) => {
      const model = modelList.find((m) => m.id === modelId);
      if (!model) return;

      const isSelected = selectedModelIds.includes(modelId);

      // Toggle off — but prevent parent removal if children are selected
      if (isSelected) {
        if (!hasSelectedChildren(model)) {
          updateSelectedModelIds((prev) => prev.filter((id) => id !== modelId));
        }
        return;
      }

      if (!areParentsSelected(model)) return;

      const sameTypeSelected = modelList.find(
        (m) => selectedModelIds.includes(m.id) && m.type === model.type,
      );

      if (sameTypeSelected) {
        setOverrideTargetModel(sameTypeSelected);
        setSelectionAlertModel({
          ...model,
          selectionType: "override",
          selectedStatus: EnrichmentModelSelectedStatus.MODEL_NOT_SELECTED,
        });
        return;
      }

      if (model.type === ModelTypes.MULTIMODEL_OBJECT_ENCODING) {
        setSelectionAlertModel({
          ...model,
          selectionType: "alert",
          selectedStatus: EnrichmentModelSelectedStatus.MODEL_NOT_SELECTED,
        });
        return;
      }

      updateSelectedModelIds((prev) => [...prev, modelId]);
    },
    [
      modelList,
      selectedModelIds,
      areParentsSelected,
      hasSelectedChildren,
      updateSelectedModelIds,
    ],
  );

  return {
    modelList,
    isModelListLoading,
    selectedModelIds,
    fetchEnrichmentModels,
    handleModelSelection,
    handleDeclineConfirmation,
    handleAcceptOverrideConfirmation,
    selectionAlertModel,
    modelDependencyMessages,
    disabledModelIds,
    triggerEnrichmentPipeline,
  };
};
